import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../context/MobileContext';
import { Button } from '../components/ui/button';
import DetailPageHeader from '../components/shared/DetailPageHeader';
import { PageTabs, PageTab } from '../components/shared/PageTabs';
import {
  Save,
  RotateCcw,
  Upload,
  Plus,
  ShieldCheck,
  Heart,
  UserPlus,
  MoreHorizontal,
  Users,
  Shield,
  LayoutGrid
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LeadersList,
  PlayersList,
  MemberEditDialog
} from '../components/team';
import AddRosterMemberDialog from '../components/team/AddRosterMemberDialog';
import RosterImportWizard from '../components/team/RosterImportWizard';
import { useTeamMembers } from '../hooks/useTeamMembers';
import PlayerSquadWorkspace from '../components/mobile/PlayerSquadWorkspace';

export default function Team() {
  const { t } = useTranslation('team_admin');
  const { user, activeTeam, hasPermission } = useAuth();
  const { isMobile } = useMobileContext();

  const teamId =
    activeTeam?.id ||
    user?.active_team_id ||
    user?.team_id;

  const {
    members,
    players,
    leaders,
    loading,
    preferences,
    hasUnsavedChanges,
    updatePreference,
    savePreferences,
    resetPreferencesToDefault,
    getRoleDisplayName,
    refreshMembers
  } = useTeamMembers(teamId, user?.id);

  const [selectedMember, setSelectedMember] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [addMode, setAddMode] = useState(null);
  const [activeRosterTab, setActiveRosterTab] = useState('all');

  const canEdit = useMemo(
    () => hasPermission('assistant_coach', teamId),
    [hasPermission, teamId]
  );

  const canImport = useMemo(
    () => hasPermission('head_coach', teamId),
    [hasPermission, teamId]
  );

  const totalPlayerCount = useMemo(() => {
    if (!Array.isArray(members)) {
      return 0;
    }

    return members.filter(
      (member) => member.role === 'player'
    ).length;
  }, [members]);

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setEditDialogOpen(true);
  };

  const handleMemberSaved = () => {
    refreshMembers();
  };

  if (isMobile) {
    return <PlayerSquadWorkspace />;
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-[rgb(var(--tn-bg-0d0f13))]"
      data-testid="team-page"
    >
      {/* Same shared page-header standard as Match / Calendar / Tactics. */}
      <div className="-mt-3 shrink-0">
        <DetailPageHeader
          title={t('roster.title')}
          subtitle={`${players.length} ${String(t('roster.players')).toLocaleLowerCase('sv-SE')} · ${leaders.length} ${String(t('roster.leaders')).toLocaleLowerCase('sv-SE')}`}
          actions={
            <div className="flex items-center gap-2">
              {canImport && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowImport(true)}
                  data-testid="import-btn"
                  className="h-9 rounded-xl border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-bg-12171d))] px-3.5 text-[12px] font-semibold text-[rgb(var(--tn-text-dde2e8))] shadow-none hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.05] hover:text-[rgb(var(--tn-text-ffffff))]"
                >
                  <Upload className="mr-1.5 h-3.5 w-3.5" />
                  {t('roster.import')}
                </Button>
              )}

              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      data-testid="add-roster-member-btn"
                      className="h-9 rounded-xl border border-[var(--t-accent)]/25 bg-[var(--tn-accent-fill)] px-4 text-[12px] font-semibold text-[#111318] shadow-none hover:bg-[var(--tn-accent-fill)]/90"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      {t('roster.add')}
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    className="min-w-[190px] border-[rgb(var(--tn-border-ffffff))]/[0.08] bg-[rgb(var(--tn-bg-12171d))] text-[rgb(var(--tn-text-f2f4f7))]"
                  >
                    <DropdownMenuItem
                      data-testid="add-roster-member-player"
                      onClick={() => setAddMode('player')}
                      className="cursor-pointer focus:bg-[rgb(var(--tn-tint-ffffff))]/[0.06] focus:text-[rgb(var(--tn-text-ffffff))]"
                    >
                      <UserPlus className="mr-2 h-4 w-4 text-[rgb(var(--tn-text-9ea8b3))]" />
                      {t('roster.add_player')}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      data-testid="add-roster-member-leader"
                      onClick={() => setAddMode('leader')}
                      className="cursor-pointer focus:bg-[rgb(var(--tn-tint-ffffff))]/[0.06] focus:text-[rgb(var(--tn-text-ffffff))]"
                    >
                      <ShieldCheck className="mr-2 h-4 w-4 text-[rgb(var(--tn-text-9ea8b3))]" />
                      {t('roster.add_leader')}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      data-testid="add-roster-member-parent"
                      onClick={() => setAddMode('parent')}
                      className="cursor-pointer focus:bg-[rgb(var(--tn-tint-ffffff))]/[0.06] focus:text-[rgb(var(--tn-text-ffffff))]"
                    >
                      <Heart className="mr-2 h-4 w-4 text-[rgb(var(--tn-text-9ea8b3))]" />
                      {t('roster.add_parent')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-xl border border-[rgb(var(--tn-border-ffffff))]/[0.06] bg-[rgb(var(--tn-bg-12171d))] text-[rgb(var(--tn-text-c7cdd5))] shadow-none hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.05] hover:text-[rgb(var(--tn-text-ffffff))]"
                    data-testid="roster-more-actions-btn"
                    aria-label="Fler alternativ"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-48 border-[rgb(var(--tn-border-ffffff))]/[0.08] bg-[rgb(var(--tn-bg-12171d))] text-[rgb(var(--tn-text-eef1f4))]"
                >
                  <DropdownMenuItem
                    onClick={savePreferences}
                    disabled={!hasUnsavedChanges}
                    data-testid="save-roster-view-btn"
                    className="cursor-pointer gap-2.5 focus:bg-[rgb(var(--tn-tint-ffffff))]/[0.06] focus:text-[rgb(var(--tn-text-ffffff))]"
                  >
                    <Save className="h-4 w-4 text-[rgb(var(--tn-text-9ea8b3))]" />
                    <span>{t('view_settings.save_view')}</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-[rgb(var(--tn-tint-ffffff))]/[0.07]" />

                  <DropdownMenuItem
                    onClick={resetPreferencesToDefault}
                    data-testid="reset-roster-view-btn"
                    className="cursor-pointer gap-2.5 focus:bg-[rgb(var(--tn-tint-ffffff))]/[0.06] focus:text-[rgb(var(--tn-text-ffffff))]"
                  >
                    <RotateCcw className="h-4 w-4 text-[rgb(var(--tn-text-9ea8b3))]" />
                    <span>{t('view_settings.reset')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
          tabs={
            <PageTabs>
              <PageTab
                onClick={() => setActiveRosterTab('all')}
                data-testid="roster-tab-all"
                active={activeRosterTab === 'all'}
              >
                <LayoutGrid className="mr-2 h-4 w-4" />
                {t('roster.all', 'Alla')}
              </PageTab>

              <PageTab
                onClick={() => setActiveRosterTab('players')}
                data-testid="roster-tab-players"
                active={activeRosterTab === 'players'}
              >
                <Users className="mr-2 h-4 w-4" />
                {t('roster.players')}
              </PageTab>

              <PageTab
                onClick={() => setActiveRosterTab('leaders')}
                data-testid="roster-tab-leaders"
                active={activeRosterTab === 'leaders'}
              >
                <Shield className="mr-2 h-4 w-4" />
                {t('roster.leaders')}
              </PageTab>
            </PageTabs>
          }
          testId="team-detail-header"
        />
      </div>

      <div className="mt-[13px] min-h-0 flex-1 overflow-y-auto pb-3">
        {activeRosterTab === 'all' && (
          <div className="flex flex-col gap-2">
            <PlayersList
              players={players}
              loading={loading}
              sortOption={preferences.playerSort}
              onSortChange={(value) =>
                updatePreference('playerSort', value)
              }
              filterOption={preferences.positionFilter}
              onFilterChange={(value) =>
                updatePreference('positionFilter', value)
              }
              onEditMember={handleEditMember}
              canEdit={canEdit}
              totalCount={totalPlayerCount}
              teamId={teamId}
              showSectionTitle
            />

            <LeadersList
              leaders={leaders}
              loading={loading}
              sortOption={preferences.leaderSort}
              onSortChange={(value) =>
                updatePreference('leaderSort', value)
              }
              getRoleDisplayName={getRoleDisplayName}
              onEditMember={handleEditMember}
              canEdit={canEdit}
              showSectionTitle
            />
          </div>
        )}

        {activeRosterTab === 'players' && (
          <PlayersList
            players={players}
            loading={loading}
            sortOption={preferences.playerSort}
            onSortChange={(value) =>
              updatePreference('playerSort', value)
            }
            filterOption={preferences.positionFilter}
            onFilterChange={(value) =>
              updatePreference('positionFilter', value)
            }
            onEditMember={handleEditMember}
            canEdit={canEdit}
            totalCount={totalPlayerCount}
            teamId={teamId}
            showSectionTitle={false}
          />
        )}

        {activeRosterTab === 'leaders' && (
          <LeadersList
            leaders={leaders}
            loading={loading}
            sortOption={preferences.leaderSort}
            onSortChange={(value) =>
              updatePreference('leaderSort', value)
            }
            getRoleDisplayName={getRoleDisplayName}
            onEditMember={handleEditMember}
            canEdit={canEdit}
            showSectionTitle={false}
          />
        )}
      </div>

      <MemberEditDialog
        member={selectedMember}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSaved={handleMemberSaved}
        teamId={teamId}
      />

      <RosterImportWizard
        open={showImport}
        onOpenChange={setShowImport}
        teamId={teamId}
        onComplete={refreshMembers}
      />

      <AddRosterMemberDialog
        open={addMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAddMode(null);
          }
        }}
        teamId={teamId}
        mode={addMode || 'player'}
        onCreated={() => {
          setAddMode(null);
          refreshMembers();
        }}
      />

    </div>
  );
}